import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { usersApi, rolesApi, adminApi } from '../api/endpoints';
import MetaIntegrationCard from '../components/MetaIntegrationCard';
import IntegrationStatusCard from '../components/IntegrationStatusCard';
import GreetingsCard from '../components/GreetingsCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// --- Configuration Constants ---
const ROLE_BADGE = {
  Admin: 'bg-rose-50 text-rose-700 border-rose-200',
  Manager: 'bg-violet-50 text-violet-700 border-violet-200',
  'Sales Agent': 'bg-blue-50 text-blue-700 border-blue-200',
  Viewer: 'bg-slate-50 text-slate-700 border-slate-200',
};

const MODULE_LABELS = {
  leads: 'Leads', contacts: 'Contacts', properties: 'Projects',
  deals: 'Deals', tasks: 'Tasks', calls: 'Calls',
  site_visits: 'Site Visits', reports: 'Reports',
  bookings: 'Bookings', payments: 'Payments',
  finance: 'Finance & Loans', registry: 'Registry Docs',
  admin: 'Admin Panel',
};

// --- Sub-Component: Permission Matrix ---
const PermissionMatrix = ({ value, onChange, allowedActionsPerModule, modules, actions }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2.5 sticky left-0 bg-slate-50">Module</th>
            {actions.map(a => <th key={a} className="px-4 py-2.5 capitalize text-center">{a}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {modules.map(m => {
            const allowed = allowedActionsPerModule[m] || actions;
            const current = value[m] || [];
            const allChecked = allowed.every(a => current.includes(a));
            return (
              <tr key={m} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 sticky left-0 bg-white">
                  <div className="font-medium text-slate-800 flex items-center gap-2">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={() => onChange({ ...value, [m]: allChecked ? [] : [...allowed] })}
                    />
                    {MODULE_LABELS[m] || m}
                  </div>
                </td>
                {actions.map(a => (
                  <td key={a} className="px-4 py-2.5 text-center">
                    {allowed.includes(a) ? (
                      <Checkbox
                        checked={current.includes(a)}
                        onCheckedChange={() => {
                          const next = current.includes(a) ? current.filter(x => x !== a) : [...current, a];
                          onChange({ ...value, [m]: next });
                        }}
                      />
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// --- Sub-Component: User Create/Edit Dialog ---
const UserDialog = ({ trigger, onSaved, presets, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'Sales Agent', phone: '', status: 'Active', permissions: {} });

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        email: initial?.email || '',
        password: '',
        role: initial?.role || 'Sales Agent',
        phone: initial?.phone || '',
        status: initial?.status || 'Active',
        permissions: initial?.permissions || presets?.roles?.[initial?.role || 'Sales Agent'] || {},
      });
      setTab('basic');
    }
  }, [open, initial, presets]);

  const submit = async () => {
    if (!form.name || !form.email) return toast.error('Name and email are required');
    if (!isEdit && !form.password) return toast.error('Password is required for new users');
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      const saved = isEdit ? await usersApi.update(initial.id, payload) : await usersApi.create(payload);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'User updated' : 'User created');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save user');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit User' : 'Create New User'}</DialogTitle>
          <DialogDescription>Assign roles and granular permissions to your team members.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="basic">Profile</TabsTrigger><TabsTrigger value="perms">Permissions</TabsTrigger></TabsList>
          {tab === 'basic' && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="col-span-2"><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Email *</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v, permissions: presets?.roles?.[v] || {}})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(presets?.roles || {}).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.status === 'Active'} onCheckedChange={v => setForm({...form, status: v ? 'Active' : 'Inactive'})} />
                <span className="text-sm font-medium">{form.status}</span>
              </div>
              <div className="col-span-2"><Label>{isEdit ? 'New Password (Optional)' : 'Password *'}</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} /></div>
            </div>
          )}
          {tab === 'perms' && presets && (
            <div className="mt-4">
              <PermissionMatrix value={form.permissions} onChange={v => setForm({...form, permissions: v})} allowedActionsPerModule={presets.allowed_actions_per_module} modules={presets.modules} actions={presets.actions} />
            </div>
          )}
        </Tabs>
        <DialogFooter className="mt-6"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save User'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Main Admin Component ---
const Admin = () => {
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [presets, setPresets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  
  // Dynamic Route Filters
  const [roleFilter, setRoleFilter] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([usersApi.list(), usersApi.rolePresets()]);
      setUsers(u); setPresets(p);
    } catch (e) { toast.error('Failed to load admin data'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Intercept routing payload from Dashboard
  useEffect(() => {
    if (location.state?.filterRole) {
      setRoleFilter(location.state.filterRole);
    } else {
      setRoleFilter(null);
    }
  }, [location.state]);

  const filtered = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase());
      const matchesRole = !roleFilter || u.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, q, roleFilter]);

  const clearFilters = () => {
    setRoleFilter(null);
    setQ('');
    navigate('.', { replace: true, state: null });
  };

  const handleUserSaved = (saved, isEdit) => {
    if (isEdit) {
      setUsers(prev => prev.map(u => u.id === saved.id ? saved : u));
    } else {
      setUsers(prev => [saved, ...prev]);
      setQ('');
    }
  };

  const onDelete = async (u) => {
    try {
      await usersApi.remove(u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      toast.success(`Removed ${u.name}`);
    } catch (e) { toast.error('Failed to delete user'); }
  };

  return (
    <div className="space-y-5 p-6">
      <div className="rounded-xl bg-gradient-to-r from-[#0F1F38] via-[#1B3A5C] to-[#2A4F7A] text-white p-6">
        <div className="flex items-center gap-3 mb-2">
          <Icons.ShieldCheck className="w-5 h-5 text-brand-gold" />
          <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-bold">Administration</div>
        </div>
        <h2 className="text-2xl font-bold">Team & Access Control</h2>
        <p className="text-slate-300 text-sm mt-1">Manage accounts and define granular permissions for your staff.</p>
      </div>

      <IntegrationStatusCard />

      {/* Filter Banner */}
      {roleFilter && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg flex items-center justify-between text-xs font-medium animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <Icons.Filter className="w-4 h-4 text-blue-600" />
            <span>Showing users with role: <strong>{roleFilter}</strong></span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-blue-700 hover:bg-blue-100 uppercase tracking-wider text-[10px]">
            Clear Filter
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[300px]">
          <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search team members..." value={q} onChange={e => setQ(e.target.value)} />
        </div>
        {presets && <UserDialog presets={presets} onSaved={handleUserSaved} trigger={<Button className="bg-brand-navy"><Icons.UserPlus className="w-4 h-4 mr-2" /> New User</Button>} />}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Permissions</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}><td colSpan={5} className="p-4"><Skeleton className="h-10 w-full" /></td></tr>
                ))
              ) : filtered.map(u => {
                const isMe = currentUser?.id === u.id;
                const isAdmin = u.role === 'Admin';
                return (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-navy text-white text-xs font-bold flex items-center justify-center">
                          {u.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800 flex items-center gap-2">
                            {u.name} {isMe && <Badge className="text-[10px] bg-brand-gold/10 text-brand-navy border-brand-gold/20">YOU</Badge>}
                          </div>
                          <div className="text-[11px] text-slate-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className={ROLE_BADGE[u.role]}>{u.role}</Badge></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.status === 'Active' ? 'text-emerald-600' : 'text-slate-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{Object.keys(u.permissions || {}).length} Modules</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {presets && <UserDialog presets={presets} initial={u} onSaved={handleUserSaved} trigger={<Button size="icon" variant="ghost"><Icons.Pencil className="w-4 h-4" /></Button>} />}
                        {!isMe && !isAdmin && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700"><Icons.Trash2 className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                <AlertDialogDescription>Are you sure you want to remove {u.name}? This cannot be undone.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-rose-600" onClick={() => onDelete(u)}>Remove</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <MetaIntegrationCard />

      <GreetingsCard />

      {/* Danger Zone: demo data cleanup */}
      <Card className="border-rose-200 bg-rose-50/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-rose-800 flex items-center gap-1.5">
              <Icons.AlertTriangle className="w-4 h-4" /> Danger Zone — Remove Demo Data
            </div>
            <div className="text-xs text-slate-600 mt-0.5 max-w-xl">
              Permanently deletes seed/demo/TEST records (leads, contacts, deals, calls, visits, demo bookings &amp; payments).
              Real business data, users and projects are protected. Cannot be undone.
            </div>
          </div>
          <Button
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-100"
            data-testid="cleanup-demo-data-btn"
            onClick={async () => {
              if (!window.confirm('Delete ALL demo/test data? Real business records are kept. This cannot be undone.')) return;
              try {
                const r = await adminApi.cleanupDemoData();
                toast.success(`Removed ${r.deleted.leads} demo leads + ${r.total_associated_deleted} linked records`);
              } catch (e) {
                console.error('Demo cleanup failed', e);
                toast.error(e?.response?.data?.detail || 'Cleanup failed');
              }
            }}
          >
            <Icons.Trash2 className="w-4 h-4 mr-1.5" /> Remove Demo Data
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-rose-200/60">
          <div>
            <div className="text-sm font-bold text-rose-800 flex items-center gap-1.5">
              <Icons.Unlock className="w-4 h-4" /> Release Demo-Booked Units
            </div>
            <div className="text-xs text-slate-600 mt-0.5 max-w-xl">
              Frees units stuck as "Booked" with no real booking record (demo seed leftovers), clears lead
              coordinators that don&apos;t match any CRM user, and re-syncs project unit counts. Safe &amp; repeatable —
              real bookings are never touched.
            </div>
          </div>
          <Button
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-100"
            data-testid="release-demo-units-btn"
            onClick={async () => {
              if (!window.confirm('Release all demo-booked units and sync project counts? Real bookings are kept.')) return;
              try {
                const r = await adminApi.releaseDemoUnits();
                toast.success(`Released ${r.units_released} units · Cleared ${r.demo_owners_cleared} demo coordinators · Synced ${r.projects_synced} projects`);
              } catch (e) {
                console.error('Release demo units failed', e);
                toast.error(e?.response?.data?.detail || 'Release failed');
              }
            }}
          >
            <Icons.Unlock className="w-4 h-4 mr-1.5" /> Release Demo Units
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Admin;